export type CandleLike = {
  open: number;
  high: number;
  low: number;
  close: number;
};

export const SPREAD_FUNDING_PRICE_FORMAT = { type: 'price' as const, precision: 4, minMove: 0.0001 };

export function formatSpreadFundingValue(value: number): string {
  if (!Number.isFinite(value)) return '-';
  return value.toFixed(4);
}

/** Decimal places for price display: >=100 → 1dp, [1,100) → 2dp, and so on. */
export function adaptivePriceDecimals(value: number): number {
  const abs = Math.abs(value);
  if (!Number.isFinite(abs) || abs === 0) return 2;
  if (abs >= 100) return 1;
  if (abs >= 1) return 2;
  if (abs >= 0.1) return 3;
  if (abs >= 0.01) return 4;
  if (abs >= 0.001) return 5;
  if (abs >= 0.0001) return 6;
  if (abs >= 0.00001) return 7;
  return 8;
}

export function formatAdaptivePrice(value: number): string {
  if (!Number.isFinite(value)) return '-';
  return value.toFixed(adaptivePriceDecimals(value));
}

export function formatSignedPercent2(value: number): string {
  if (!Number.isFinite(value)) return '-';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

export function formatPercent2(value: number): string {
  if (!Number.isFinite(value)) return '-';
  return `${value.toFixed(2)}%`;
}

export function candleChangePercent(candle: Pick<CandleLike, 'open' | 'close'>): number {
  if (!Number.isFinite(candle.open) || candle.open === 0) return 0;
  return ((candle.close - candle.open) / candle.open) * 100;
}

export function candleRangePercent(candle: Pick<CandleLike, 'open' | 'high' | 'low'>): number {
  if (!Number.isFinite(candle.open) || candle.open === 0) return 0;
  return ((candle.high - candle.low) / candle.open) * 100;
}

/** Compact K-line tooltip: open/close/change%/range%. */
export function formatCandleTooltipValue(candle: CandleLike): string {
  const changePct = candleChangePercent(candle);
  const rangePct = candleRangePercent(candle);
  return `${formatAdaptivePrice(candle.open)}/${formatAdaptivePrice(candle.close)}/${formatPercent2(changePct)}/${formatPercent2(rangePct)}`;
}
